/**
 * Generic, domain-agnostic type checking utilities.
 *
 * Conventions, usage examples, and rules for this file are documented in typechecking.md.
 * When adding a new method, document it there too.
 *
 * Keep this file free of any application-specific concepts (entities, routines, apps, etc.).
 * It will be extracted to a shared library and used in contexts where those concepts do not exist.
 * Domain-specific assertions belong as static methods on the relevant domain class instead
 * (e.g. App.get(appName).assertArrayOfEntitiesHavingValidType). See typechecking.md for details.
 */
export default class DataTypes {

  // ------- //
  // Strings //
  // ------- //

  /**
   * Asserts that val is a primitive string. Rejects boxed String objects (new String(...)).
   */
  static assertString(val) {
    if (typeof val !== "string" || val instanceof String) {
      throw new Error(`expected a primitive string, got ${val === null ? "null" : typeof val}`);
    }
  }

  // ------ //
  // Arrays //
  // ------ //

  /**
   * Asserts that val is an array.
   */
  static assertArray(val) {
    if (!Array.isArray(val)) {
      throw new Error(`expected an array, got ${val === null ? "null" : typeof val}`);
    }
  }

  /**
   * Asserts that val is an array and that every element is an instance of Class.
   */
  static assertArrayOf(val, Class) {
    this.assertArray(val);
    val.forEach((item, index) => {
      if (!(item instanceof Class)) {
        throw new Error(
          `expected every element to be an instance of ${Class.name}, `
          + `but element at index ${index} is ${item?.constructor?.name ?? typeof item}`,
        );
      }
    });
  }

  /**
   * Asserts that val is an array and that every element is a primitive string.
   */
  static assertArrayOfStrings(val) {
    this.assertArray(val);
    val.forEach((item, index) => {
      if (typeof item !== "string" || item instanceof String) {
        throw new Error(`expected every element to be a primitive string, but element at index ${index} is ${item === null ? "null" : typeof item}`);
      }
    });
  }

  // -------- //
  // integers //
  // -------- //

  /**
   * Asserts that val is a finite number (rejects NaN, Infinity, and non-number types).
   */
  static assertNumber(val) {
    if (typeof val !== "number" || !Number.isFinite(val)) {
      throw new Error(`expected a finite number, got ${val === null ? "null" : typeof val}`);
    }
  }

  /**
   * Asserts that val is an integer (not a float, not NaN, not Infinity).
   */
  static assertInteger(val) {
    if (!Number.isInteger(val)) {
      throw new Error(`expected an integer, got ${val === null ? "null" : typeof val}`);
    }
  }

  // -------- //
  // Booleans //
  // -------- //

  /**
   * Asserts that val is a primitive boolean.
   */
  static assertBoolean(val) {
    if (typeof val !== "boolean") {
      throw new Error(`expected a boolean, got ${val === null ? "null" : typeof val}`);
    }
  }

  // ------- //
  // Objects //
  // ------- //

  /**
   * Asserts that val is a plain object — not null, not an array, not a class instance.
   */
  static assertObject(val) {
    if (val === null || typeof val !== "object" || Array.isArray(val)) {
      const label = val === null ? "null" : Array.isArray(val) ? "array" : typeof val;
      throw new Error(`expected a plain object, got ${label}`);
    }
  }

  /**
   * Asserts that obj contains key, with no constraint on the value's type.
   * Use this when a key is required but any value (including null or undefined) is acceptable.
   * Prefer assertObjectKeyExistsOfType when the type is known.
   */
  static assertObjectKeyExists(key, obj) {
    this.assertObject(obj);
    if (!Object.hasOwn(obj, key)) {
      throw new Error(`expected object to have key "${key}"`);
    }
  }

  /**
   * Asserts that obj contains key and that obj[key] satisfies assertFn.
   * assertFn should be one of the other DataTypes methods (or any function that throws on invalid input).
   *
   * @example
   * DataTypes.assertObjectKeyExistsOfType("name", config, DataTypes.assertString);
   * DataTypes.assertObjectKeyExistsOfType("options", config, DataTypes.assertObject);
   */
  static assertObjectKeyExistsOfType(key, obj, assertFn) {
    this.assertObject(obj);
    if (!Object.hasOwn(obj, key)) {
      throw new Error(`expected object to have key "${key}"`);
    }
    assertFn(obj[key]);
  }

  /**
   * Asserts that val is an instance of Class.
   */
  static assertInstanceOf(val, Class) {
    if (!(val instanceof Class)) {
      throw new Error(`expected an instance of ${Class.name}, got ${val?.constructor?.name ?? typeof val}`);
    }
  }
}
